import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface UseScrollHiddenItemCountsOptions<TElement extends HTMLElement> {
  isEnabled?: boolean;
  itemSelector: string;
  scrollContainerRef: RefObject<TElement | null>;
}

const EMPTY_SCROLL_HIDDEN_ITEM_COUNTS = { above: 0, below: 0 };
// Sub-pixel scroll/zoom values can leave items within 1px of the edge; treat those as hidden.
const SCROLL_BOUNDARY_TOLERANCE_PX = 1;
type ScrollHiddenItemPlacement = keyof typeof EMPTY_SCROLL_HIDDEN_ITEM_COUNTS;

const getVisibleScrollItems = (scrollElement: HTMLElement, itemSelector: string) =>
  Array.from(scrollElement.querySelectorAll<HTMLElement>(itemSelector)).filter(element => (
    element.getClientRects().length > 0
  ));

const getScrollBehavior = (ownerWindow: Window | null): ScrollBehavior => (
  ownerWindow?.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
);

const getHiddenItemCounts = (scrollElement: HTMLElement | null, itemSelector: string) => {
  if (
    !scrollElement
    || scrollElement.scrollHeight <= scrollElement.clientHeight + SCROLL_BOUNDARY_TOLERANCE_PX
  ) {
    return EMPTY_SCROLL_HIDDEN_ITEM_COUNTS;
  }

  const items = getVisibleScrollItems(scrollElement, itemSelector);

  if (!items.length) {
    return EMPTY_SCROLL_HIDDEN_ITEM_COUNTS;
  }

  const scrollRect = scrollElement.getBoundingClientRect();
  let hiddenAbove = 0;
  let hiddenBelow = 0;

  items.forEach(item => {
    const itemRect = item.getBoundingClientRect();

    if (itemRect.bottom <= scrollRect.top + SCROLL_BOUNDARY_TOLERANCE_PX) {
      hiddenAbove += 1;
    }

    if (itemRect.top >= scrollRect.bottom - SCROLL_BOUNDARY_TOLERANCE_PX) {
      hiddenBelow += 1;
    }
  });

  return { above: hiddenAbove, below: hiddenBelow };
};

export const useScrollHiddenItemCounts = <TElement extends HTMLElement = HTMLElement>({
  isEnabled = true,
  itemSelector,
  scrollContainerRef,
}: UseScrollHiddenItemCountsOptions<TElement>) => {
  const [hiddenItemCounts, setHiddenItemCounts] = useState(EMPTY_SCROLL_HIDDEN_ITEM_COUNTS);
  const animationFrameRef = useRef<number | null>(null);

  const measureHiddenItemCounts = useCallback(() => {
    const nextCounts = getHiddenItemCounts(scrollContainerRef.current, itemSelector);

    setHiddenItemCounts(previousCounts => (
      previousCounts.above === nextCounts.above && previousCounts.below === nextCounts.below
        ? previousCounts
        : nextCounts
    ));
  }, [itemSelector, scrollContainerRef]);

  const cancelScheduledHiddenItemCountsUpdate = useCallback(() => {
    const ownerWindow = scrollContainerRef.current?.ownerDocument.defaultView ?? window;

    if (animationFrameRef.current !== null) {
      ownerWindow.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, [scrollContainerRef]);

  const scheduleHiddenItemCountsUpdate = useCallback(() => {
    const ownerWindow = scrollContainerRef.current?.ownerDocument.defaultView ?? window;

    cancelScheduledHiddenItemCountsUpdate();
    animationFrameRef.current = ownerWindow.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      measureHiddenItemCounts();
    });
  }, [cancelScheduledHiddenItemCountsUpdate, measureHiddenItemCounts, scrollContainerRef]);

  const scrollToHiddenItems = useCallback((placement: ScrollHiddenItemPlacement) => {
    const scrollElement = scrollContainerRef.current;

    if (!scrollElement) {
      return;
    }

    const scrollRect = scrollElement.getBoundingClientRect();
    const items = getVisibleScrollItems(scrollElement, itemSelector);
    const hiddenAboveItems = items.filter(item => (
      item.getBoundingClientRect().bottom <= scrollRect.top + SCROLL_BOUNDARY_TOLERANCE_PX
    ));
    const targetItem = placement === 'above'
      ? hiddenAboveItems[hiddenAboveItems.length - 1]
      : items.find(item => item.getBoundingClientRect().top >= scrollRect.bottom - SCROLL_BOUNDARY_TOLERANCE_PX);

    const ownerWindow = scrollElement.ownerDocument.defaultView;

    targetItem?.scrollIntoView({
      behavior: getScrollBehavior(ownerWindow),
      block: 'nearest',
    });
    scheduleHiddenItemCountsUpdate();
  }, [itemSelector, scheduleHiddenItemCountsUpdate, scrollContainerRef]);

  useEffect(() => {
    if (!isEnabled) {
      cancelScheduledHiddenItemCountsUpdate();
      setHiddenItemCounts(EMPTY_SCROLL_HIDDEN_ITEM_COUNTS);
      return undefined;
    }

    const scrollElement = scrollContainerRef.current;

    if (!scrollElement) {
      return undefined;
    }

    const ownerWindow = scrollElement.ownerDocument.defaultView ?? window;
    const ResizeObserverConstructor = ownerWindow.ResizeObserver;
    const MutationObserverConstructor = ownerWindow.MutationObserver;
    const resizeObserver = ResizeObserverConstructor
      ? new ResizeObserverConstructor(scheduleHiddenItemCountsUpdate)
      : null;
    const mutationObserver = MutationObserverConstructor
      ? new MutationObserverConstructor(scheduleHiddenItemCountsUpdate)
      : null;

    scheduleHiddenItemCountsUpdate();
    resizeObserver?.observe(scrollElement);
    mutationObserver?.observe(scrollElement, { childList: true, subtree: true });

    return () => {
      cancelScheduledHiddenItemCountsUpdate();
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [
    cancelScheduledHiddenItemCountsUpdate,
    isEnabled,
    scheduleHiddenItemCountsUpdate,
    scrollContainerRef,
  ]);

  return {
    hiddenItemCounts,
    scrollToHiddenItems,
    updateHiddenItemCounts: scheduleHiddenItemCountsUpdate,
  };
};
